/**
 * Chart Data Transform Utilities
 *
 * Provides functionality to transform label casing for better readability
 * while preserving proper casing for specific terms.
 */

// Types
interface DataRow {
  metadata?: {
    name?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

type DataProcessor = (data: DataRow[]) => DataRow[];

// Terms that should preserve their original casing
const DEFAULT_CASING_EXCEPTIONS = [
  'AC',
  'Android',
  'DC',
  'HDMI',
  'USB',
  'USB-C',
  'USB-A',
  'USB-B',
  'USB Type A',
  'USB Type B',
  'USB Type C',
  'Wi-Fi',
  'Bluetooth',
  'LED',
  'LCD',
  'RAM',
  'ROM',
  'SSD',
  'HDD',
  'CPU',
  'GPU',
  'TV',
  'PC',
  'DVD',
  'CD',
  'BD',
  'API',
  'SDK',
  'AI',
  'ML',
  'IoT',
  'VR',
  'AR',
  'QR',
  'pH',
  'mAh',
  'GB',
  'MB',
  'TB',
  'KB',
  'GHz',
  'MHz',
  'BCAA',
  'iPad',
  'iPhone',
  'iPod',
  'iMac',
  'iOS',
  'macOS',
  'MacBook',
  'AirPods',
  'AirTag',
  'VGA',
  'DVI',
  'Thunderbolt',
  'PCI',
  'Afghanistan',
  'Anguilla',
  'Brunei Darussalam',
  'Cambodia',
  'Canada',
  'Chile',
  'China',
  'France',
  'Germany',
  'Hong Kong',
  'Israel',
  'Italy',
  'Japan',
  'Liechtenstein',
  'Malaysia',
  'Mexico',
  'Philippines',
  'Slovenia',
  'South Korea',
  'Spain',
  'Sweden',
  'Switzerland',
  'Taiwan',
  'Texas',
  'Thailand',
  'United Kingdom',
  'United States',
  'Vietnam'
];
/**
 * Creates a data preprocessor that transforms label casing for better readability.
 *
 * @param selectedGroupByField - The currently selected group by field
 * @param customExceptions - Optional custom exceptions to use instead of defaults
 * @returns A function that processes data arrays
 *
 * @example
 * const processor = createLabelCaseProcessor('productAttributeValue');
 
 */
export const createLabelCaseProcessor = (
  selectedGroupByField: string | undefined,
  customExceptions?: string[]
): DataProcessor => {
  // Only process productAttributeValue fields
  if (selectedGroupByField !== 'productAttributeValue') {
    return (data: DataRow[]) => data;
  }

  const exceptions = new Set(customExceptions || DEFAULT_CASING_EXCEPTIONS);

  return (data: DataRow[]) => {
    if (!data) return data;

    return data.map((row) => ({
      ...row,
      metadata: {
        ...row.metadata,
        name: row.metadata?.name ? transformLabelCase(row.metadata.name, exceptions) : row.metadata?.name
      }
    }));
  };
};

/**
 * Transforms a label to lowercase while preserving exception terms
 */
function transformLabelCase(label: string, exceptions: Set<string>): string {
  // First check if the entire label is an exception
  const exactMatch = findException(label, exceptions);
  if (exactMatch) return exactMatch;

  // Check for multi-word exceptions by trying progressively smaller phrases
  const words = label.split(/\s+/);
  let result = '';
  let i = 0;

  while (i < words.length) {
    // Try to match the longest possible phrase starting at current position
    let matchFound = false;

    for (let j = words.length; j > i; j--) {
      const phrase = words.slice(i, j).join(' ');
      const exception = findException(phrase, exceptions);

      if (exception) {
        result += (i > 0 ? ' ' : '') + exception;
        i = j;
        matchFound = true;
        break;
      }
    }

    if (!matchFound) {
      const word = words[i];

      // Handle hyphenated words
      if (word.includes('-')) {
        const processedWord = word
          .split('-')
          .map((part) => findException(part, exceptions) || part.toLowerCase())
          .join('-');
        result += (i > 0 ? ' ' : '') + processedWord;
      } else {
        // Check single word or convert to lowercase
        const exception = findException(word, exceptions);
        result += (i > 0 ? ' ' : '') + (exception || word.toLowerCase());
      }

      i++;
    }
  }

  return result;
}

/**
 * Finds a matching exception for a word (case-insensitive)
 * Returns the properly cased version if found
 */
function findException(word: string, exceptions: Set<string>): string | null {
  // Direct match (fast path)
  if (exceptions.has(word)) return word;

  // Case-insensitive search
  const upperWord = word.toUpperCase();
  for (const exception of exceptions) {
    if (exception.toUpperCase() === upperWord) {
      return exception;
    }
  }

  return null;
}

// Export exceptions for reuse or extension
export { DEFAULT_CASING_EXCEPTIONS };
